import { createContext, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';

import { InternalAtmosphericLogGroup } from 'Containers/RocketDry/types';

import { projectSelector, propertySelector } from 'Containers/RocketScan/selectors';

export const RocketDryContext = createContext({});

export const RocketDryFunctions = () => {
  const project = useSelector(projectSelector, areEqual);
  const property = useSelector(propertySelector, areEqual);

  // common state
  const [isEquipmentFolder, setIsEquipmentFolder] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState(undefined);
  const [selectedRoomArea, setSelectedRoomArea] = useState(undefined);

  // project/external atmospheric log
  const [isViewAtmosphericLogModalOpen, setIsViewAtmosphericLogModalOpen] = useState(false);
  const [isAddExternalAtmosphericLogModalOpen, setIsAddExternalAtmosphericLogModalOpen] = useState(false);
  const [isEditExternalAtmosphericLogModalOpen, setIsEditExternalAtmosphericLogModalOpen] = useState(false);
  const [isDeleteAtmosphericLogModalOpen, setIsDeleteAtmosphericLogModalOpen] = useState(false);
  const [isAddMoistureLogModalOpen, setIsAddMoistureLogModalOpen] = useState(false);
  const [selectedAtmosphericLog, setSelectedAtmosphericLog] = useState<any>(undefined);
  const [selectedAtmosphericLogType, setSelectedAtmosphericLogType] = useState(undefined);

  // room/internal atmospheric log
  const [isAddInternalAtmosphericLogModalOpen, setIsAddInternalAtmosphericLogModalOpen] = useState(false);
  const [isEditInternalAtmosphericLogModalOpen, setIsEditInternalAtmosphericLogModalOpen] = useState(false);
  const [isDeleteInternalAtmosphericLogModalOpen, setIsDeleteInternalAtmosphericLogModalOpen] = useState(false);
  const [selectedAtmosphericLogGroup, setSelectedAtmosphericLogGroup] =
    useState<InternalAtmosphericLogGroup>(undefined);
  const [internalAtmosphericLogCreated, setInternalAtmosphericLogCreated] = useState(false);
  const [internalAtmosphericLogUpdated, setInternalAtmosphericLogUpdated] = useState(false);
  const [internalAtmosphericLogDeleted, setInternalAtmosphericLogDeleted] = useState(false);

  // moisture log
  const [isViewMoistureLogModalOpen, setIsViewMoistureLogModalOpen] = useState(false);
  const [isEditMoistureLogReadingModalOpen, setIsEditMoistureLogReadingModalOpen] = useState(false);
  const [isEditMoistureLogGoalModalOpen, setIsEditMoistureLogGoalModalOpen] = useState(false);
  const [isDeleteMoistureLogModalOpen, setIsDeleteMoistureLogModalOpen] = useState(false);
  const [selectedMoistureLog, setSelectedMoistureLog] = useState<any>(undefined);
  const [isAddMoistureLogMaterialModalOpen, setIsAddMoistureLogMaterialModalOpen] = useState(false);
  const [isAddMoistureLogReadingModalOpen, setIsAddMoistureLogReadingModalOpen] = useState(false);
  const [selectedDamageMaterial, setSelectedDamageMaterial] = useState<any>(undefined);
  const [moistureLogRefetch, setMoistureLogRefetch] = useState(false);

  // equipment
  const [isAddEquipmentModalOpen, setIsAddEquipmentModalOpen] = useState(false);
  const [isDeleteEquipmentModalOpen, setIsDeleteEquipmentModalOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<any>({});
  const [equipmentAdded, setEquipmentAdded] = useState(false);
  const [equipmentUpdatedRoomId, setEquipmentUpdatedRoomId] = useState(undefined);
  const [equipmentDeletedRoomId, setEquipmentDeletedRoomId] = useState(undefined);

  return {
    // common
    project,
    property,
    selectedRoomId,
    selectedRoomArea,
    setSelectedRoomId,
    setSelectedRoomArea,
    isEquipmentFolder,
    setIsEquipmentFolder,

    // project/external atmospheric log
    isViewAtmosphericLogModalOpen,
    setIsViewAtmosphericLogModalOpen,
    isAddExternalAtmosphericLogModalOpen,
    setIsAddExternalAtmosphericLogModalOpen,
    isEditExternalAtmosphericLogModalOpen,
    setIsEditExternalAtmosphericLogModalOpen,
    isDeleteAtmosphericLogModalOpen,
    setIsDeleteAtmosphericLogModalOpen,
    selectedAtmosphericLog,
    selectedAtmosphericLogGroup,
    selectedAtmosphericLogType,
    setSelectedAtmosphericLog,
    setSelectedAtmosphericLogGroup,
    setSelectedAtmosphericLogType,

    // room/internal atmospheric log
    isAddInternalAtmosphericLogModalOpen,
    setIsAddInternalAtmosphericLogModalOpen,
    isEditInternalAtmosphericLogModalOpen,
    setIsEditInternalAtmosphericLogModalOpen,
    isDeleteInternalAtmosphericLogModalOpen,
    setIsDeleteInternalAtmosphericLogModalOpen,
    internalAtmosphericLogCreated,
    setInternalAtmosphericLogCreated,
    internalAtmosphericLogUpdated,
    setInternalAtmosphericLogUpdated,
    internalAtmosphericLogDeleted,
    setInternalAtmosphericLogDeleted,

    // moisture log
    selectedMoistureLog,
    setSelectedMoistureLog,
    isViewMoistureLogModalOpen,
    setIsViewMoistureLogModalOpen,
    isEditMoistureLogReadingModalOpen,
    setIsEditMoistureLogReadingModalOpen,
    isEditMoistureLogGoalModalOpen,
    setIsEditMoistureLogGoalModalOpen,
    isDeleteMoistureLogModalOpen,
    setIsDeleteMoistureLogModalOpen,
    isAddMoistureLogModalOpen,
    setIsAddMoistureLogModalOpen,
    isAddMoistureLogMaterialModalOpen,
    setIsAddMoistureLogMaterialModalOpen,
    isAddMoistureLogReadingModalOpen,
    setIsAddMoistureLogReadingModalOpen,
    selectedDamageMaterial,
    setSelectedDamageMaterial,
    moistureLogRefetch,
    setMoistureLogRefetch,

    // equipment
    isAddEquipmentModalOpen,
    setIsAddEquipmentModalOpen,
    isDeleteEquipmentModalOpen,
    setIsDeleteEquipmentModalOpen,
    selectedEquipment,
    setSelectedEquipment,
    equipmentAdded,
    setEquipmentAdded,
    equipmentUpdatedRoomId,
    setEquipmentUpdatedRoomId,
    equipmentDeletedRoomId,
    setEquipmentDeletedRoomId,
  };
};

export const useRocketDryFunctions = () => useContext(RocketDryContext);
